import { supabase } from '@/integrations/supabase/client';

/**
 * Build storage path attempts for face snapshots (visit_faces, faces bucket).
 */
export function buildStoragePathAttempts(imagePath: string): Array<[string, string]> {
  const publicUrlMatch = imagePath.match(
    /^https?:\/\/[^/]+\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)$/i
  );
  const rawPath = (publicUrlMatch?.[2] || imagePath)
    .replace(/^public\/[^/]+\//, '')
    .replace(/^sign\/[^/]+\//, '')
    .replace(/^\/+/, '');

  const strippedPath = rawPath
    .replace(/^faces\//, '')
    .replace(/^visit-faces\//, '')
    .replace(/^visit_faces\//, '');

  const attempts: Array<[string, string]> = [];
  if (publicUrlMatch?.[1]) {
    attempts.push([publicUrlMatch[1], rawPath]);
    attempts.push([publicUrlMatch[1], strippedPath]);
  }
  attempts.push(['faces', rawPath]);
  if (strippedPath !== rawPath) {
    attempts.push(['faces', strippedPath]);
  }
  attempts.push(['visit-faces', strippedPath]);
  attempts.push(['visit_faces', strippedPath]);

  const seen = new Set<string>();
  return attempts.filter(([bucket, path]) => {
    const key = `${bucket}::${path}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Get a signed URL for a face snapshot path.
 */
export async function getFaceImageUrl(imagePath: string | null): Promise<string | null> {
  if (!imagePath) return null;

  const uniqueAttempts = buildStoragePathAttempts(imagePath);

  for (const [bucket, path] of uniqueAttempts) {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, 3600);

      if (!error && data?.signedUrl) {
        return data.signedUrl;
      }
    } catch {
      // Try next
    }
  }

  return null;
}
